import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import api, { ticketsAPI, analyticsAPI, usersAPI } from '@/lib/api';
import { formatRelativeTime, priorityColors, statusColors } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import {
  Headphones,
  Inbox,
  TicketIcon,
  Users,
  BookOpen,
  BarChart3,
  Settings,
  LogOut,
  Search,
  Filter,
  Plus,
  MessageSquare,
  Clock,
  ChevronRight,
  ChevronLeft,
  AlertCircle,
  CheckCircle2,
  Loader2,
  UserCog,
  User,
  FileText,
  Menu,
  X,
  Building2,
  Package,
  Trash2,
  FolderOpen,
  Home,
  MapPin,
  Mail,
  Tag,
  Briefcase,
  MonitorCheck,
  Calendar as CalendarIcon
} from 'lucide-react';
import TicketDetail from '@/components/TicketDetail';
import TicketsView from '@/components/TicketsView';
import CalendarView from '@/components/CalendarView';
import NewTicketDialog from '@/components/NewTicketDialog';
import UserManagement from '@/components/UserManagement';
import CustomersModule from '@/components/CustomersModule';
import SitesModule from '@/components/SitesModule';
import WorkOrdersModule from '@/components/WorkOrdersModule';
import DepartmentManagement from '@/components/DepartmentManagement';
import InventoryManagement from '@/components/InventoryManagement';
import DocumentationModule from '@/components/DocumentationModule';
import NotificationBell from '@/components/NotificationBell';
import GoogleDriveBackup from '@/components/GoogleDriveBackup';
import ClockWidget from '@/components/ClockWidget';
import TimeTracking from '@/components/TimeTracking';
import HRPortal from '@/components/hr/HRPortal';
import HRAdmin from '@/components/hr/HRAdmin';
import ScheduleModule from '@/components/schedule/ScheduleModule';
import RMMDashboard from '@/components/rmm/RMMDashboard';
import LocationPinger from '@/components/LocationPinger';
import LocationPingSettings from '@/components/LocationPingSettings';
import AgentLocationCrumbs from '@/components/AgentLocationCrumbs';
import CategoriesSettings from '@/components/CategoriesSettings';
import EmailSettings from '@/components/EmailSettings';
import CompanySettings from '@/components/CompanySettings';
import KnowledgeBase from '@/pages/KnowledgeBase';

// Embedded KB — renders the same component inside the dashboard shell (no
// own top header, since the sidebar already provides navigation).
const KnowledgeBaseEmbedded = () => <KnowledgeBase embedded />;

// Enhanced Customer Detail View Component
function CustomerDetailView({ customer, onBack }) {
  const [tickets, setTickets] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadCustomerData = useCallback(async () => {
    try {
      setLoading(true);
      // Load customer's tickets
      const ticketsRes = await ticketsAPI.list({ customer_id: customer.id });
      setTickets(ticketsRes.data);
      
      // Load KB articles (most recent for now)
      try {
        const articlesRes = await api.get('/knowledge-base/articles');
        setArticles(articlesRes.data.slice(0, 5)); // Top 5 articles
      } catch (err) {
        console.log('KB articles not available');
        setArticles([]);
      }
    } catch (err) {
      console.error('Failed to load customer data:', err);
    } finally {
      setLoading(false);
    }
  }, [customer.id]);

  useEffect(() => {
    loadCustomerData();
  }, [loadCustomerData]);

  return (
    <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto">
      <Button 
        variant="ghost" 
        onClick={onBack}
        className="mb-4 md:mb-6 text-slate-600 min-h-[44px] hover:bg-slate-100"
      >
        ← Back to Customers
      </Button>

      {/* Customer Header */}
      <Card className="bg-white border-slate-200 mb-4 md:mb-6">
        <CardContent className="p-4 md:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <Avatar className="w-16 h-16 border-2 border-slate-200">
              <AvatarImage src={customer.avatar_url} />
              <AvatarFallback className="bg-cyan-100 text-cyan-600 text-xl">
                {customer.name.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <h2 className="text-xl md:text-2xl font-bold text-slate-800">{customer.name}</h2>
              <div className="flex flex-wrap items-center gap-2 md:gap-4 mt-2 text-sm text-slate-600">
                <span className="break-all">{customer.email}</span>
                {customer.username && <span>@{customer.username}</span>}
                {customer.customer_id && <Badge variant="outline" className="text-xs">{customer.customer_id}</Badge>}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
        </div>
      ) : (
        <div className="space-y-4 md:space-y-6">
          {/* Customer's Tickets */}
          <Card className="bg-white border-slate-200">
            <CardHeader className="p-4 md:p-6">
              <CardTitle className="text-lg md:text-xl text-slate-800 flex items-center gap-2">
                <TicketIcon className="w-5 h-5" />
                Customer Tickets ({tickets.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 md:p-6 pt-0">
              {tickets.length === 0 ? (
                <div className="text-center py-8 text-slate-400">
                  <TicketIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="text-sm">No tickets found for this customer</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {tickets.map((ticket) => (
                    <div 
                      key={ticket.id} 
                      className="p-4 border border-slate-200 rounded-lg hover:border-cyan-300 transition-colors active:scale-[0.98]"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-1">
                            <span className="text-xs font-mono text-slate-500">{ticket.ticket_number}</span>
                            <Badge className={`text-[10px] ${priorityColors[ticket.priority]}`}>
                              {ticket.priority}
                            </Badge>
                            <Badge className={`text-[10px] ${statusColors[ticket.status]}`}>
                              {ticket.status.replace('_', ' ')}
                            </Badge>
                          </div>
                          <h3 className="font-medium text-slate-800 mb-1">{ticket.subject}</h3>
                          <p className="text-sm text-slate-600 line-clamp-2">{ticket.description}</p>
                          <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
                            <Clock className="w-3 h-3" />
                            {formatRelativeTime(ticket.created_at)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Related KB Articles */}
          {articles.length > 0 && (
            <Card className="bg-white border-slate-200">
              <CardHeader className="p-4 md:p-6">
                <CardTitle className="text-lg md:text-xl text-slate-800 flex items-center gap-2">
                  <BookOpen className="w-5 h-5" />
                  Related Knowledge Base Articles
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 md:p-6 pt-0">
                <div className="space-y-3">
                  {articles.map((article) => (
                    <div 
                      key={article.id} 
                      className="p-4 border border-slate-200 rounded-lg hover:border-cyan-300 transition-colors cursor-pointer active:scale-[0.98]"
                    >
                      <h3 className="font-medium text-slate-800 mb-1">{article.title}</h3>
                      <p className="text-sm text-slate-600 line-clamp-2">{article.excerpt || article.content?.substring(0, 150)}</p>
                      {article.category && (
                        <Badge variant="outline" className="mt-2 text-xs">{article.category}</Badge>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

// Customers View Component with Detail Support
function CustomersView() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    try {
      setLoading(true);
      const res = await usersAPI.list({ role: 'customer' });
      setCustomers(res.data);
    } catch (err) {
      console.error('Failed to load customers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDeleteCustomers = async () => {
    if (selectedCustomerIds.length === 0) return;
    
    if (!confirm(`Delete ${selectedCustomerIds.length} customer(s)? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await usersAPI.bulkDelete(selectedCustomerIds);
      setSelectedCustomerIds([]);
      await loadCustomers();
    } catch (err) {
      console.error('Failed to delete customers:', err);
      alert('Failed to delete customers');
    }
  };

  const toggleCustomerSelection = (customerId) => {
    setSelectedCustomerIds(prev =>
      prev.includes(customerId)
        ? prev.filter(id => id !== customerId)
        : [...prev, customerId]
    );
  };

  const toggleSelectAllCustomers = () => {
    const filtered = filteredCustomers;
    if (selectedCustomerIds.length === filtered.length) {
      setSelectedCustomerIds([]);
    } else {
      setSelectedCustomerIds(filtered.map(c => c.id));
    }
  };

  const filteredCustomers = customers.filter(c =>
    searchQuery === '' ||
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.customer_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Show detail view if customer selected
  if (selectedCustomer) {
    return (
      <CustomerDetailView 
        customer={selectedCustomer} 
        onBack={() => setSelectedCustomer(null)}
      />
    );
  }

  return (
    <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto">
      <div className="mb-4 md:mb-6">
        <h1 className="text-xl md:text-2xl font-bold mb-2 text-slate-800">
          Customers
          {selectedCustomerIds.length > 0 && (
            <span className="ml-2 text-sm font-normal text-cyan-600">
              ({selectedCustomerIds.length} selected)
            </span>
          )}
        </h1>
        <p className="text-sm md:text-base text-slate-500">Manage customer accounts and view their tickets</p>
      </div>

      <div className="mb-4 md:mb-6 flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search customers..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-white border-slate-200 h-11 md:h-10"
          />
        </div>
        {selectedCustomerIds.length > 0 && (
          <Button
            variant="destructive"
            onClick={handleBulkDeleteCustomers}
            className="min-h-[44px] md:min-h-10"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete ({selectedCustomerIds.length})
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-4 md:px-6 py-3 text-left">
                    <Checkbox
                      checked={selectedCustomerIds.length === filteredCustomers.length && filteredCustomers.length > 0}
                      onCheckedChange={toggleSelectAllCustomers}
                    />
                  </th>
                  <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Customer</th>
                  <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Customer ID</th>
                  <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Email</th>
                  <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Username</th>
                  <th className="px-4 md:px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Joined</th>
                  <th className="px-4 md:px-6 py-3 text-right text-xs font-medium text-slate-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {filteredCustomers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-slate-50">
                    <td className="px-4 md:px-6 py-4">
                      <Checkbox
                        checked={selectedCustomerIds.includes(customer.id)}
                        onCheckedChange={() => toggleCustomerSelection(customer.id)}
                      />
                    </td>
                    <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <Avatar className="w-8 h-8">
                          <AvatarImage src={customer.avatar_url} />
                          <AvatarFallback className="bg-cyan-100 text-cyan-600 text-xs">
                            {customer.name.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm font-medium text-slate-800">{customer.name}</span>
                      </div>
                    </td>
                    <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-mono text-slate-600">{customer.customer_id || '-'}</span>
                    </td>
                    <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-slate-600">{customer.email}</span>
                    </td>
                    <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-slate-600">@{customer.username || '-'}</span>
                    </td>
                    <td className="px-4 md:px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-slate-500">{new Date(customer.created_at).toLocaleDateString()}</span>
                    </td>
                    <td className="px-4 md:px-6 py-4 whitespace-nowrap text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedCustomer(customer)}
                        className="text-cyan-600 hover:text-cyan-700 min-h-[36px]"
                      >
                        <ChevronRight className="w-4 h-4 mr-1" />
                        View Details
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden divide-y divide-slate-200">
            {filteredCustomers.map((customer) => (
              <div 
                key={customer.id} 
                className="p-4 flex items-start gap-3"
              >
                <Checkbox
                  checked={selectedCustomerIds.includes(customer.id)}
                  onCheckedChange={() => toggleCustomerSelection(customer.id)}
                  className="mt-1"
                />
                <div
                  className="flex-1 active:bg-slate-50 transition-colors"
                  onClick={() => setSelectedCustomer(customer)}
                >
                <div className="flex items-start gap-3 mb-3">
                  <Avatar className="w-12 h-12">
                    <AvatarImage src={customer.avatar_url} />
                    <AvatarFallback className="bg-cyan-100 text-cyan-600">
                      {customer.name.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-slate-800 mb-1">{customer.name}</h3>
                    <p className="text-sm text-slate-600 truncate">{customer.email}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                </div>
                <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                  {customer.customer_id && (
                    <Badge variant="outline" className="font-mono">{customer.customer_id}</Badge>
                  )}
                  {customer.username && (
                    <span>@{customer.username}</span>
                  )}
                  <span>Joined {new Date(customer.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            </div>
            ))}
          </div>

          {filteredCustomers.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-sm">No customers found</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Settings View Component
function SettingsView() {
  const { user, isAdmin } = useAuth();
  const [activeSection, setActiveSection] = useState('profile');

  return (
    <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto">
      <div className="mb-4 md:mb-6">
        <h1 className="text-2xl md:text-3xl font-bold mb-1 md:mb-2 text-slate-800">Settings</h1>
        <p className="text-sm md:text-base text-slate-500">Manage your account and preferences</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
        {/* Settings Navigation */}
        <div className="lg:col-span-3">
          <Card className="bg-white border-slate-200">
            <CardContent className="p-2 md:p-3">
              <nav className="flex lg:flex-col gap-1 overflow-x-auto" role="tablist">
                <button
                  onClick={() => setActiveSection('profile')}
                  role="tab"
                  aria-selected={activeSection === 'profile'}
                  className={`shrink-0 lg:w-full text-left px-3 py-2.5 rounded-lg text-sm whitespace-nowrap transition-colors ${
                    activeSection === 'profile'
                      ? 'bg-cyan-50 text-cyan-600 font-medium'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid="settings-profile-nav"
                >
                  <User className="w-4 h-4 inline mr-2" />
                  Profile
                </button>
                <button
                  onClick={() => setActiveSection('notifications')}
                  role="tab"
                  aria-selected={activeSection === 'notifications'}
                  className={`shrink-0 lg:w-full text-left px-3 py-2.5 rounded-lg text-sm whitespace-nowrap transition-colors ${
                    activeSection === 'notifications'
                      ? 'bg-cyan-50 text-cyan-600 font-medium'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <AlertCircle className="w-4 h-4 inline mr-2" />
                  Notifications
                </button>
                <button
                  onClick={() => setActiveSection('appearance')}
                  role="tab"
                  aria-selected={activeSection === 'appearance'}
                  className={`shrink-0 lg:w-full text-left px-3 py-2.5 rounded-lg text-sm whitespace-nowrap transition-colors ${
                    activeSection === 'appearance'
                      ? 'bg-cyan-50 text-cyan-600 font-medium'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <Settings className="w-4 h-4 inline mr-2" />
                  Appearance
                </button>
                {isAdmin && (
                  <button
                    onClick={() => setActiveSection('categories')}
                    role="tab"
                    aria-selected={activeSection === 'categories'}
                    className={`shrink-0 lg:w-full text-left px-3 py-2.5 rounded-lg text-sm whitespace-nowrap transition-colors ${
                      activeSection === 'categories'
                        ? 'bg-cyan-50 text-cyan-600 font-medium'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid="settings-categories-nav"
                  >
                    <Tag className="w-4 h-4 inline mr-2" />
                    Categories
                  </button>
                )}
                {isAdmin && (
                  <button
                    onClick={() => setActiveSection('company')}
                    role="tab"
                    aria-selected={activeSection === 'company'}
                    className={`shrink-0 lg:w-full text-left px-3 py-2.5 rounded-lg text-sm whitespace-nowrap transition-colors ${
                      activeSection === 'company'
                        ? 'bg-cyan-50 text-cyan-600 font-medium'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid="settings-company-nav"
                  >
                    <Building2 className="w-4 h-4 inline mr-2" />
                    Company
                  </button>
                )}
                {isAdmin && (
                  <button
                    onClick={() => setActiveSection('email')}
                    role="tab"
                    aria-selected={activeSection === 'email'}
                    className={`shrink-0 lg:w-full text-left px-3 py-2.5 rounded-lg text-sm whitespace-nowrap transition-colors ${
                      activeSection === 'email'
                        ? 'bg-cyan-50 text-cyan-600 font-medium'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid="settings-email-nav"
                  >
                    <Mail className="w-4 h-4 inline mr-2" />
                    Email
                  </button>
                )}
                {isAdmin && (
                  <button
                    onClick={() => setActiveSection('location-ping')}
                    role="tab"
                    aria-selected={activeSection === 'location-ping'}
                    className={`shrink-0 lg:w-full text-left px-3 py-2.5 rounded-lg text-sm whitespace-nowrap transition-colors ${
                      activeSection === 'location-ping'
                        ? 'bg-cyan-50 text-cyan-600 font-medium'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                    data-testid="settings-location-ping-nav"
                  >
                    <MapPin className="w-4 h-4 inline mr-2" />
                    Location Ping
                  </button>
                )}
              </nav>
            </CardContent>
          </Card>
        </div>

        {/* Settings Content */}
        <div className="lg:col-span-9">
          {activeSection === 'profile' && (
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-slate-800">Profile Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4 pb-4 border-b border-slate-200">
                  <Avatar className="w-16 h-16 border-2 border-slate-200">
                    <AvatarImage src={user?.avatar_url} />
                    <AvatarFallback className="bg-cyan-100 text-cyan-600 text-xl">
                      {user?.name?.charAt(0)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold text-slate-800">{user?.name}</h3>
                    <p className="text-sm text-slate-500 capitalize">{user?.role}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <Label className="text-slate-700 text-xs">Email</Label>
                    <Input
                      value={user?.email || ''}
                      disabled
                      className="bg-slate-50 border-slate-200 text-slate-600"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-700 text-xs">Role</Label>
                    <Input
                      value={user?.role || ''}
                      disabled
                      className="bg-slate-50 border-slate-200 text-slate-600 capitalize"
                    />
                  </div>
                  {user?.username && (
                    <div>
                      <Label className="text-slate-700 text-xs">Username</Label>
                      <Input
                        value={`@${user.username}`}
                        disabled
                        className="bg-slate-50 border-slate-200 text-slate-600"
                      />
                    </div>
                  )}
                  {user?.customer_id && (
                    <div>
                      <Label className="text-slate-700 text-xs">Customer ID</Label>
                      <Input
                        value={user.customer_id}
                        disabled
                        className="bg-slate-50 border-slate-200 text-slate-600 font-mono"
                      />
                    </div>
                  )}
                </div>

                <div className="pt-4 border-t border-slate-200">
                  <Button className="bg-cyan-500 hover:bg-cyan-600 rounded-full" disabled>
                    Update Profile
                  </Button>
                  <p className="text-xs text-slate-400 mt-2">Contact administrator to update your profile</p>
                </div>
              </CardContent>
            </Card>
          )}

          {activeSection === 'notifications' && (
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-slate-800">Notification Preferences</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-slate-200">
                  <div>
                    <p className="font-medium text-slate-800">Email Notifications</p>
                    <p className="text-sm text-slate-500">Receive email updates for ticket activity</p>
                  </div>
                  <Checkbox defaultChecked />
                </div>
                <div className="flex items-center justify-between py-3 border-b border-slate-200">
                  <div>
                    <p className="font-medium text-slate-800">New Ticket Alerts</p>
                    <p className="text-sm text-slate-500">Get notified when new tickets are assigned</p>
                  </div>
                  <Checkbox defaultChecked />
                </div>
                <div className="flex items-center justify-between py-3 border-b border-slate-200">
                  <div>
                    <p className="font-medium text-slate-800">Status Updates</p>
                    <p className="text-sm text-slate-500">Notifications when ticket status changes</p>
                  </div>
                  <Checkbox defaultChecked />
                </div>
                <div className="pt-4">
                  <Button className="bg-cyan-500 hover:bg-cyan-600 rounded-full" disabled>
                    Save Preferences
                  </Button>
                  <p className="text-xs text-slate-400 mt-2">Feature coming soon</p>
                </div>
              </CardContent>
            </Card>
          )}

          {activeSection === 'appearance' && (
            <Card className="bg-white border-slate-200">
              <CardHeader>
                <CardTitle className="text-slate-800">Appearance Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-slate-700">Theme</Label>
                  <Select defaultValue="light" disabled>
                    <SelectTrigger className="bg-white border-slate-200">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="light">Light</SelectItem>
                      <SelectItem value="dark">Dark</SelectItem>
                      <SelectItem value="auto">Auto</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-400 mt-1">Currently using light theme</p>
                </div>
                <div className="pt-4">
                  <Button className="bg-cyan-500 hover:bg-cyan-600 rounded-full" disabled>
                    Save Changes
                  </Button>
                  <p className="text-xs text-slate-400 mt-2">Feature coming soon</p>
                </div>
              </CardContent>
            </Card>
          )}

          {activeSection === 'categories' && isAdmin && (
            <CategoriesSettings />
          )}

          {activeSection === 'company' && isAdmin && (
            <CompanySettings />
          )}

          {activeSection === 'email' && isAdmin && (
            <EmailSettings />
          )}

          {activeSection === 'location-ping' && isAdmin && (
            <LocationPingSettings />
          )}
        </div>
      </div>
    </div>
  );
}

const sidebarItems = [
  { icon: BarChart3, label: 'Dashboard', path: '/dashboard' },
  { icon: TicketIcon, label: 'Ticketing System', path: '/dashboard/tickets' },
  { icon: CalendarIcon, label: 'Calendar', path: '/dashboard/calendar' },
  { icon: MapPin, label: 'Live Map & Trails', path: '/dashboard/locations', adminOnly: true },
  { icon: FileText, label: 'Work Orders', path: '/dashboard/work-orders' },
  { icon: FolderOpen, label: 'Documentation', path: '/dashboard/documentation', adminOrAgentOnly: true },
  { icon: Package, label: 'Inventory', path: '/dashboard/inventory', adminOrAgentOnly: true },
  { icon: Users, label: 'Customers', path: '/dashboard/customers', adminOnly: true },
  { icon: Home, label: 'Sites', path: '/dashboard/sites', adminOrAgentOnly: true },
  { icon: BookOpen, label: 'Knowledge Base', path: '/dashboard/knowledge' },
  { icon: BarChart3, label: 'Analytics', path: '/dashboard/analytics' },
  { icon: Clock, label: 'Time Tracking', path: '/dashboard/time', adminOrAgentOnly: true },
  { icon: CalendarIcon, label: 'Schedule', path: '/dashboard/schedule', adminOrAgentOnly: true },
  { icon: Briefcase, label: 'HR Portal', path: '/dashboard/hr', adminOrAgentOnly: true },
  { icon: Briefcase, label: 'HR Admin', path: '/dashboard/hr-admin', adminOnly: true },
  { icon: MonitorCheck, label: 'RMM', path: '/dashboard/rmm', adminOrAgentOnly: true },
  { icon: Building2, label: 'Departments', path: '/dashboard/departments', adminOrAgentOnly: true },
  { icon: UserCog, label: 'User Management', path: '/dashboard/users', adminOnly: true },
  { icon: Settings, label: 'Settings', path: '/dashboard/settings' },
];

export default function AgentDashboard() {
  const { user, logout, isAdmin, isAgent } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [ticketScope, setTicketScope] = useState('all'); // 'all' | 'mine'
  const [searchQuery, setSearchQuery] = useState('');
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [currentView, setCurrentView] = useState('dashboard');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showTicketList, setShowTicketList] = useState(true); // Toggle ticket list on mobile
  const [selectedTicketIds, setSelectedTicketIds] = useState([]); // For bulk delete

  useEffect(() => {
    // Check route and set view accordingly
    const path = location.pathname;
    // Clear any selected ticket when leaving ticket / calendar routes
    if (!['/dashboard/tickets', '/dashboard/calendar'].includes(path)) {
      setSelectedTicket(null);
    }
    if (path === '/dashboard/users') {
      setCurrentView('users');
    } else if (path === '/dashboard/customers') {
      setCurrentView('customers');
    } else if (path === '/dashboard/sites') {
      setCurrentView('sites');
    } else if (path === '/dashboard/work-orders') {
      setCurrentView('work-orders');
    } else if (path === '/dashboard/departments') {
      setCurrentView('departments');
    } else if (path === '/dashboard/inventory') {
      setCurrentView('inventory');
    } else if (path === '/dashboard/documentation') {
      setCurrentView('documentation');
    } else if (path === '/dashboard/settings') {
      setCurrentView('settings');
    } else if (path === '/dashboard/time') {
      setCurrentView('time');
    } else if (path === '/dashboard/schedule') {
      setCurrentView('schedule');
    } else if (path === '/dashboard/hr') {
      setCurrentView('hr');
    } else if (path === '/dashboard/hr-admin') {
      setCurrentView('hr-admin');
    } else if (path === '/dashboard/rmm') {
      setCurrentView('rmm');
    } else if (path === '/dashboard/calendar') {
      setCurrentView('calendar');
    } else if (path === '/dashboard/locations') {
      setCurrentView('locations');
    } else if (path === '/dashboard/knowledge' || path.startsWith('/dashboard/knowledge/')) {
      setCurrentView('knowledge');
    } else if (path === '/dashboard/live-map') {
      // Legacy redirect — Live Map merged into Live Map & Trails (Iter 25).
      navigate('/dashboard/locations', { replace: true });
      return;
    } else if (path === '/dashboard/queue') {
      navigate('/dashboard/tickets', { replace: true });
      return;
    } else if (path === '/dashboard/tickets') {
      setCurrentView('tickets');
      const params = new URLSearchParams(location.search);
      const scope = params.get('scope');
      if (scope === 'mine' || scope === 'all') {
        setTicketScope(scope);
      }
    } else {
      setCurrentView('dashboard');
    }
  }, [location.pathname, location.search]);

  useEffect(() => {
    loadData();
  }, [statusFilter, priorityFilter, ticketScope]);

  const loadData = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (priorityFilter !== 'all') params.priority = priorityFilter;
      if (ticketScope === 'mine') params.assigned_to_me = true;
      if (ticketScope === 'open') params.status = 'open';
      if (ticketScope === 'closed') {
        params.status = 'closed';
        params.include_closed = true;
      }
      
      const [ticketsRes, analyticsRes] = await Promise.all([
        ticketsAPI.list(params),
        analyticsAPI.getOverview()
      ]);
      
      setTickets(ticketsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const filteredTickets = tickets.filter(t => 
    searchQuery === '' || 
    t.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.ticket_number.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleTicketUpdated = () => {
    loadData();
    if (selectedTicket) {
      ticketsAPI.get(selectedTicket.id).then(res => setSelectedTicket(res.data));
    }
  };


  const handleBulkDeleteTickets = async () => {
    if (selectedTicketIds.length === 0) return;
    
    if (!confirm(`Delete ${selectedTicketIds.length} ticket(s)? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await ticketsAPI.bulkDelete(selectedTicketIds);
      setSelectedTicketIds([]);
      await loadData();
    } catch (err) {
      console.error('Failed to delete tickets:', err);
      alert('Failed to delete tickets');
    }
  };

  const handleClaimTicketInline = async (e, ticketId) => {
    e.stopPropagation();
    try {
      await ticketsAPI.claim(ticketId);
      await loadData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to claim ticket');
    }
  };

  const toggleTicketSelection = (ticketId) => {
    setSelectedTicketIds(prev =>
      prev.includes(ticketId)
        ? prev.filter(id => id !== ticketId)
        : [...prev, ticketId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedTicketIds.length === filteredTickets.length) {
      setSelectedTicketIds([]);
    } else {
      setSelectedTicketIds(filteredTickets.map(t => t.id));
    }
  };

  // Sidebar Navigation Component (reusable for desktop and mobile)
  const SidebarNav = ({ onLinkClick }) => (
    <>
      <div className="p-4 border-b border-slate-200">
        <Link to="/" className="flex items-center gap-2" onClick={onLinkClick}>
          <img 
            src="https://customer-assets.emergentagent.com/job_project-builder-158/artifacts/n1qmhcgm_Logo.png" 
            alt="MVP IT Logo" 
            className="h-8 w-auto"
          />
        </Link>
      </div>

      <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
        {sidebarItems.map((item) => {
          if (item.adminOnly && !isAdmin) return null;
          if (item.adminOrAgentOnly && user?.role === 'customer') return null;
          
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => {
                // Always clear any open ticket detail when the user uses
                // the sidebar — otherwise clicking the same nav item
                // (e.g. "Ticketing System" while viewing a ticket) does
                // nothing because the URL doesn't change and the
                // route-driven useEffect won't fire.
                setSelectedTicket(null);
                onLinkClick?.();
              }}
              className={`flex items-center gap-3 px-3 py-3 md:py-2.5 rounded-xl text-sm transition-all duration-200 min-h-[44px] md:min-h-0 ${
                isActive 
                  ? 'bg-cyan-50 text-cyan-600 font-medium' 
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900 active:scale-[0.98]'
              }`}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
            >
              <item.icon className="w-5 h-5 md:w-4 md:h-4" />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>

      <div className="p-3 border-t border-slate-200">
        <ClockWidget />
        <div className="flex items-center gap-3 p-2 mt-2">
          <Avatar className="w-10 h-10 md:w-9 md:h-9 border-2 border-slate-200">
            <AvatarImage src={user?.avatar_url} />
            <AvatarFallback className="bg-cyan-100 text-cyan-600">{user?.name?.charAt(0)}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-800 truncate">{user?.name}</p>
            <p className="text-xs text-slate-500 capitalize">{user?.role}</p>
          </div>
          <div className="hidden md:flex items-center gap-1">
            <NotificationBell />
            <Button 
              variant="ghost" 
              size="icon" 
              className="text-slate-400 hover:text-slate-600 min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0"
              onClick={() => {
                handleLogout();
                onLinkClick?.();
              }}
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5 md:w-4 md:h-4" />
            </Button>
          </div>
        </div>
      </div>
    </>
  );

  // Full-page ticket view — when a ticket is selected on the tickets or
  // calendar route, hide the dashboard sidebar entirely and stretch the
  // detail to fill the viewport. A "Back to tickets" button at the top-left
  // returns to the list.
  const isFullPageTicket =
    !!selectedTicket && (currentView === 'tickets' || currentView === 'calendar');

  if (isFullPageTicket) {
    const backTarget = currentView === 'calendar' ? '/dashboard/calendar' : '/dashboard/tickets';
    const backLabel = currentView === 'calendar' ? 'Back to calendar' : 'Back to tickets';
    return (
      <div className="h-screen bg-slate-50 flex flex-col overflow-hidden" data-testid="ticket-fullpage">
        <LocationPinger />
        <div className="flex items-center gap-3 px-4 md:px-6 py-3 border-b border-slate-200 bg-white">
          <Button
            variant="ghost"
            size="sm"
            className="rounded-full text-slate-600 hover:text-slate-900"
            onClick={() => {
              setSelectedTicket(null);
              setShowTicketList(true);
              navigate(backTarget);
            }}
            data-testid="ticket-fullpage-back-btn"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            {backLabel}
          </Button>
          <div className="flex-1" />
          <NotificationBell />
        </div>
        <div className="flex-1 min-h-0 overflow-hidden">
          <TicketDetail
            ticket={selectedTicket}
            onUpdate={handleTicketUpdated}
            onClose={() => {
              setSelectedTicket(null);
              setShowTicketList(true);
              navigate(backTarget);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-slate-50 flex flex-col lg:grid lg:grid-cols-12 lg:grid-rows-1 overflow-hidden" data-testid="agent-dashboard">
      {/* Background location-ping (no UI) */}
      <LocationPinger />
      {/* Mobile Header */}
      <div className="lg:hidden flex items-center justify-between p-4 border-b border-slate-200 bg-white">
        <Link to="/" className="flex items-center gap-2">
          <img 
            src="https://customer-assets.emergentagent.com/job_project-builder-158/artifacts/n1qmhcgm_Logo.png" 
            alt="MVP IT Logo" 
            className="h-8 w-auto"
          />
        </Link>
        
        <div className="flex items-center gap-2">
          <NotificationBell />
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="min-h-[44px] min-w-[44px]">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[280px] p-0 flex flex-col">
              <SidebarNav onLinkClick={() => setMobileMenuOpen(false)} />
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Desktop Sidebar - Hidden on mobile */}
      <aside className="hidden lg:flex lg:col-span-2 border-r border-slate-200 bg-slate-50 flex-col min-h-0 lg:h-screen overflow-y-auto">
        <SidebarNav onLinkClick={() => {}} />
      </aside>

      {/* Main Content - takes the rest. min-w-0 is required so the grid
          column actually constrains the inner content width — otherwise
          long tables / wide filter rows force the column to grow and
          overflow the viewport on /dashboard/tickets and similar pages. */}
      <main className="flex-1 bg-slate-50 flex flex-col overflow-hidden lg:col-span-10 min-w-0 min-h-0 lg:h-screen lg:max-h-screen">
        {currentView === 'users' ? (
          <UserManagement />
        ) : currentView === 'customers' ? (
          <CustomersModule />
        ) : currentView === 'sites' ? (
          <SitesModule />
        ) : currentView === 'work-orders' ? (
          <WorkOrdersModule />
        ) : currentView === 'departments' ? (
          <DepartmentManagement />
        ) : currentView === 'inventory' ? (
          <InventoryManagement />
        ) : currentView === 'documentation' ? (
          <DocumentationModule />
        ) : currentView === 'time' ? (
          <TimeTracking />
        ) : currentView === 'schedule' ? (
          <ScheduleModule />
        ) : currentView === 'hr' ? (
          <HRPortal />
        ) : currentView === 'hr-admin' && isAdmin ? (
          <HRAdmin />
        ) : currentView === 'rmm' ? (
          <RMMDashboard />
        ) : currentView === 'locations' ? (
          <AgentLocationCrumbs />
        ) : currentView === 'knowledge' ? (
          <KnowledgeBaseEmbedded />
        ) : currentView === 'backup' ? (
          <GoogleDriveBackup />
        ) : currentView === 'settings' ? (
          <SettingsView />
        ) : currentView === 'calendar' ? (
          <CalendarView onSelectTicket={(t) => setSelectedTicket(t)} />
        ) : currentView === 'tickets' ? (
          <TicketsView onSelectTicket={(t) => setSelectedTicket(t)} />
        ) : (
          <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto">
            {/* Dashboard Overview */}
            <div className="mb-6 md:mb-8">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-2 text-slate-800">Welcome back, {user?.name}</h1>
              <p className="text-sm md:text-base text-slate-500">Here's an overview of your support system</p>
            </div>

            {analytics && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-6 md:mb-8">
                {/* Open Tickets Card - Clickable */}
                <Card 
                  className="bg-white border-slate-200 shadow-sm active:scale-[0.98] transition-transform cursor-pointer hover:shadow-md hover:border-cyan-300" 
                  data-testid="stat-open-tickets"
                  onClick={() => {
                    setCurrentView('tickets');
                    setStatusFilter('open');
                    setSelectedTicket(null);
                  }}
                >
                  <CardContent className="p-4 md:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Open Tickets</p>
                        <p className="text-2xl md:text-3xl font-bold text-slate-800">{analytics.open_tickets}</p>
                      </div>
                      <div className="w-12 h-12 md:w-10 md:h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                        <AlertCircle className="w-6 h-6 md:w-5 md:h-5 text-blue-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Total Tickets Card - Clickable */}
                <Card 
                  className="bg-white border-slate-200 shadow-sm active:scale-[0.98] transition-transform cursor-pointer hover:shadow-md hover:border-cyan-300" 
                  data-testid="stat-total-tickets"
                  onClick={() => {
                    setCurrentView('tickets');
                    setStatusFilter('all');
                    setSelectedTicket(null);
                  }}
                >
                  <CardContent className="p-4 md:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Total Tickets</p>
                        <p className="text-2xl md:text-3xl font-bold text-slate-800">{analytics.total_tickets}</p>
                      </div>
                      <div className="w-12 h-12 md:w-10 md:h-10 rounded-xl bg-cyan-100 flex items-center justify-center">
                        <TicketIcon className="w-6 h-6 md:w-5 md:h-5 text-cyan-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Customers Card - Clickable */}
                <Card 
                  className="bg-white border-slate-200 shadow-sm active:scale-[0.98] transition-transform cursor-pointer hover:shadow-md hover:border-cyan-300" 
                  data-testid="stat-customers"
                  onClick={() => {
                    navigate('/dashboard/customers');
                  }}
                >
                  <CardContent className="p-4 md:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Customers</p>
                        <p className="text-2xl md:text-3xl font-bold text-slate-800">{analytics.total_customers}</p>
                      </div>
                      <div className="w-12 h-12 md:w-10 md:h-10 rounded-xl bg-teal-100 flex items-center justify-center">
                        <Users className="w-6 h-6 md:w-5 md:h-5 text-teal-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* KB Articles Card - Clickable */}
                <Card 
                  className="bg-white border-slate-200 shadow-sm active:scale-[0.98] transition-transform cursor-pointer hover:shadow-md hover:border-cyan-300" 
                  data-testid="stat-articles"
                  onClick={() => {
                    navigate('/knowledge');
                  }}
                >
                  <CardContent className="p-4 md:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">KB Articles</p>
                        <p className="text-2xl md:text-3xl font-bold text-slate-800">{analytics.total_articles}</p>
                      </div>
                      <div className="w-12 h-12 md:w-10 md:h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                        <BookOpen className="w-6 h-6 md:w-5 md:h-5 text-emerald-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Work Orders Card - Clickable */}
                <Card 
                  className="bg-white border-slate-200 shadow-sm active:scale-[0.98] transition-transform cursor-pointer hover:shadow-md hover:border-cyan-300" 
                  data-testid="stat-work-orders"
                  onClick={() => {
                    navigate('/dashboard/work-orders');
                  }}
                >
                  <CardContent className="p-4 md:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Work Orders</p>
                        <p className="text-2xl md:text-3xl font-bold text-slate-800">{analytics.total_work_orders || 0}</p>
                      </div>
                      <div className="w-12 h-12 md:w-10 md:h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                        <FileText className="w-6 h-6 md:w-5 md:h-5 text-purple-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Inventory Card - Clickable */}
                <Card 
                  className="bg-white border-slate-200 shadow-sm active:scale-[0.98] transition-transform cursor-pointer hover:shadow-md hover:border-cyan-300" 
                  data-testid="stat-inventory"
                  onClick={() => {
                    navigate('/dashboard/inventory');
                  }}
                >
                  <CardContent className="p-4 md:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Inventory Items</p>
                        <p className="text-2xl md:text-3xl font-bold text-slate-800">{analytics.total_inventory_items || 0}</p>
                      </div>
                      <div className="w-12 h-12 md:w-10 md:h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                        <Package className="w-6 h-6 md:w-5 md:h-5 text-amber-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Documentation Card - Clickable */}
                <Card 
                  className="bg-white border-slate-200 shadow-sm active:scale-[0.98] transition-transform cursor-pointer hover:shadow-md hover:border-cyan-300" 
                  data-testid="stat-documentation"
                  onClick={() => {
                    navigate('/dashboard/documentation');
                  }}
                >
                  <CardContent className="p-4 md:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">IT Documentation</p>
                        <p className="text-2xl md:text-3xl font-bold text-slate-800">{analytics.total_documents || 0}</p>
                      </div>
                      <div className="w-12 h-12 md:w-10 md:h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                        <FolderOpen className="w-6 h-6 md:w-5 md:h-5 text-indigo-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Time Tracking Card - Clickable */}
                <Card
                  className="bg-white border-slate-200 shadow-sm active:scale-[0.98] transition-transform cursor-pointer hover:shadow-md hover:border-cyan-300"
                  data-testid="stat-time-tracking"
                  onClick={() => {
                    navigate('/dashboard/time');
                  }}
                >
                  <CardContent className="p-4 md:p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Time Tracking</p>
                        <p className="text-lg md:text-xl font-bold text-slate-800">Clock In / Out</p>
                      </div>
                      <div className="w-12 h-12 md:w-10 md:h-10 rounded-xl bg-cyan-100 flex items-center justify-center">
                        <Clock className="w-6 h-6 md:w-5 md:h-5 text-cyan-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}
      </main>

      <NewTicketDialog 
        open={showNewTicket} 
        onOpenChange={setShowNewTicket}
        currentUser={user}
        onCreated={(ticket) => {
          loadData();
          setSelectedTicket(ticket);
        }}
      />
    </div>
  );
}
